import React from 'react';
import Helmet from 'react-helmet';
import { ThemeProvider } from 'styled-components';
import { getSrcset, externalLink } from '../../ui/helpers';
import { BREAKPOINTS, MAIN_MENU_HEIGHT } from '../../ui/settings';
import useWindowSize from '../../hooks/useWindowSize';
import OImage from '../OImage';
import { ButtonLinkSmall } from '../../ui/Button';

import { themes } from '../../ui/design-tokens';
import Wrapper from '../../ui/Wrappers';
import Chevron from '-!svg-react-loader!../../ui/svgs/icon-chevron.svg';
import { Container, Content, Small, TextZone, ImageZone, Image } from './styles';
//import BillboardDivider from '../../images/billboard-divider.png';
import BillboardDivider2025 from '../../images/billboard-divider2025.png';

interface IProps {
  theme?: string;
  size?: string;
  title?: string;
  claim?: string;
  date?: string;
  cta?: { uri: string; title: string };
  breadcrumbs?: { language: string; countryCode: string; pageHref: any; additionalLevel?: any };
  imageUrl?: any;
  imageMobileUrl?: any;
  imageAlt?: string;
  secondMenu?: boolean;
}

const Billboard = ({
  theme,
  size,
  title,
  claim,
  cta,
  date,
  imageUrl,
  imageMobileUrl,
  imageAlt,
  secondMenu = false
}: IProps) => {
  const cleanedTheme = theme ? theme : 'neutral_dark';
  const isGradient = cleanedTheme.endsWith('_gradient') || cleanedTheme === 'neutral_black';
  const vpSize = useWindowSize();

  return (
    <ThemeProvider theme={themes[isGradient ? 'gradients' : 'billboard'][cleanedTheme]}>
      {size === 'billboard-none' || size === 'billboard-small' ? (
        <Container as={Small} gradient={isGradient}>
          <Wrapper size="medium">
            {title && <h1 className="title">{title}</h1>}
            {date && <p>{date}</p>}
            {cta && cta?.uri && cta?.title && (
              <div className="ctaContainer">
                <ButtonLinkSmall
                  className="cta"
                  as={externalLink(cta.uri) && 'a'}
                  to={externalLink(cta.uri) ? null : cta.uri}
                  href={externalLink(cta.uri) ? cta.uri : null}
                  target={externalLink(cta.uri) ? '_blank' : undefined}
                  rel={externalLink(cta.uri) && 'nofollow noopener noreferrer'}
                  data-gtm-event
                  data-gtm-category="cta"
                  data-gtm-action="contacto_b2c"
                  data-gtm-label={cta.title}>
                  {cta.title}
                </ButtonLinkSmall>
              </div>
            )}
          </Wrapper>
          <Image bg={BillboardDivider2025} className="image" aria-hidden="true" alt="" />
        </Container>
      ) : (
        <Container full={size === 'billboard-big'} gradient={isGradient} secondMenu={secondMenu}>
          <Helmet>
            {imageUrl && imageUrl.original?.href && (
              <link
                rel="preload"
                as="image"
                href={imageUrl.original?.href}
                imageSrcSet={getSrcset(imageUrl, true)}
                imagesizes="(-webkit-min-device-pixel-ratio: 2) 50vw, (min-resolution: 192dpi) 50vw, (min-resolution: 2dppx) 50vw, (-webkit-min-device-pixel-ratio: 3) 33.33vw, (min-resolution: 288dpi) 33.33vw, (min-resolution: 3dppx) 33.33vw"
              />
            )}
          </Helmet>
          <Wrapper as={Content} size="xlarge" full={size === 'billboard-big'}>
            {vpSize.width >= BREAKPOINTS.medium ? (
              imageUrl &&
              imageUrl.original?.href && (
                <ImageZone
                  ratio={imageUrl.original?.meta.width / imageUrl.original?.meta.height}
                  full={size === 'billboard-big'}>
                  <OImage
                    className="image"
                    aspectRatio={imageUrl.original?.meta.width / imageUrl.original?.meta.height}
                    src={imageUrl.original?.href}
                    srcSet={getSrcset(imageUrl)}
                    webp={getSrcset(imageUrl, true)}
                    alt={imageAlt || ''}
                    lazy={false}
                  />
                </ImageZone>
              )
            ) : imageMobileUrl && imageMobileUrl.original?.href ? (
              <ImageZone
                ratio={imageMobileUrl.original?.meta.width / imageMobileUrl.original?.meta.height}
                full={size === 'billboard-big'}>
                <OImage
                  className="image"
                  aspectRatio={
                    imageMobileUrl.original?.meta.width / imageMobileUrl.original?.meta.height
                  }
                  src={imageMobileUrl.original?.href}
                  srcSet={getSrcset(imageMobileUrl)}
                  webp={getSrcset(imageMobileUrl, true)}
                  alt={imageAlt || ''}
                  lazy={false}
                />
              </ImageZone>
            ) : (
              imageUrl &&
              imageUrl.original?.href && (
                <ImageZone
                  ratio={imageUrl?.original?.meta.width / imageUrl.original?.meta.height}
                  full={size === 'billboard-big'}>
                  <OImage
                    className="image"
                    aspectRatio={imageUrl?.original?.meta.width / imageUrl?.original?.meta.height}
                    src={imageUrl?.original?.href}
                    srcSet={getSrcset(imageUrl)}
                    webp={getSrcset(imageUrl, true)}
                    alt={imageAlt || ''}
                    lazy={false}
                  />
                </ImageZone>
              )
            )}
            <TextZone full={size === 'billboard-big'}>
              {size != 'billboard-big' && claim && <p className="claim">{claim}</p>}
              {title && <h1 className="title">{title}</h1>}
              {size === 'billboard-big' && claim && <p className="claim">{claim}</p>}
              {cta && cta?.uri && cta?.title && (
                <div className="ctaContainer">
                  <ButtonLinkSmall
                    className="cta"
                    as={externalLink(cta.uri) && 'a'}
                    to={externalLink(cta.uri) ? null : cta.uri}
                    href={externalLink(cta.uri) ? cta.uri : null}
                    target={externalLink(cta.uri) ? '_blank' : undefined}
                    rel={externalLink(cta.uri) && 'nofollow noopener noreferrer'}
                    data-gtm-event
                    data-gtm-category="cta"
                    data-gtm-action="contacto_b2c"
                    data-gtm-label={cta.title}>
                    {cta.title}
                  </ButtonLinkSmall>
                </div>
              )}
            </TextZone>
            <div className="containerCta vert-move">
              <button
                className="cta"
                onClick={() => {
                  if (!window) return;
                  window.scrollTo({
                    top:
                      Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) >
                        BREAKPOINTS.medium
                        ? Math.max(
                          document.documentElement.clientHeight || 0,
                          window.innerHeight || 0
                        ) - MAIN_MENU_HEIGHT
                        : Math.max(
                          document.documentElement.clientHeight || 0,
                          window.innerHeight || 0
                        ) -
                        MAIN_MENU_HEIGHT -
                        32,
                    behavior: 'smooth'
                  })
                }}>
                <Chevron className="svg" />
              </button>
            </div>
          </Wrapper>
          {size === 'billboard-medium' && (
            <Image bg={BillboardDivider2025} className="image" aria-hidden="true" alt="" />
          )}
          {size === 'billboard-personalizado' && cleanedTheme !== 'neutral_black' && (
            <Image bg={BillboardDivider2025} className="image" aria-hidden="true" alt="" />
          )}


        </Container>
      )}
    </ThemeProvider>
  );
};

export default Billboard;
